import { motion, AnimatePresence } from 'framer-motion';
import {
  Ticket,
  GitPullRequest,
  Search,
  BarChart3,
  FileText,
  Compass,
} from 'lucide-react';
import type { Assignment } from '../../lib/tryit-types';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Ticket,
  GitPullRequest,
  Search,
  BarChart3,
  FileText,
  Compass,
};

const categoryColors: Record<string, string> = {
  jira: 'border-blue-500/30 hover:border-blue-500/60',
  github: 'border-purple-500/30 hover:border-purple-500/60',
  project: 'border-green-500/30 hover:border-green-500/60',
  multi: 'border-amber-500/30 hover:border-amber-500/60',
};

const categoryBadgeColors: Record<string, string> = {
  jira: 'bg-blue-500/10 text-blue-400',
  github: 'bg-purple-500/10 text-purple-400',
  project: 'bg-green-500/10 text-green-400',
  multi: 'bg-amber-500/10 text-amber-400',
};

export const assignments: Assignment[] = [
  {
    id: 'jira-open',
    title: 'Open Jira Tickets',
    description: 'See your current sprint workload',
    prompt: 'What are my open Jira tickets?',
    icon: 'Ticket',
    category: 'jira',
  },
  {
    id: 'github-prs',
    title: 'PR Review Status',
    description: 'Check open PRs and reviews',
    prompt: 'Show me the open PRs and their review status',
    icon: 'GitPullRequest',
    category: 'github',
  },
  {
    id: 'search-auth',
    title: 'Search Issues',
    description: 'Cross-tool search for auth issues',
    prompt: 'Search for issues related to authentication across Jira and GitHub',
    icon: 'Search',
    category: 'multi',
  },
  {
    id: 'sprint-progress',
    title: 'Sprint Progress',
    description: 'Current sprint tracking',
    prompt: "What's the current sprint progress? Show me a breakdown.",
    icon: 'BarChart3',
    category: 'jira',
  },
  {
    id: 'read-claude-md',
    title: 'Review CLAUDE.md',
    description: 'Read and analyze project config',
    prompt:
      'Read the CLAUDE.md file and tell me what the project setup looks like.',
    icon: 'FileText',
    category: 'project',
  },
  {
    id: 'what-next',
    title: "What's Next?",
    description: 'Multi-tool reasoning across all data',
    prompt:
      "Based on the current sprint status, open PRs, and project phase — what should I work on next?",
    icon: 'Compass',
    category: 'multi',
  },
];

interface AssignmentCardsProps {
  onSelect: (assignment: Assignment) => void;
  visible: boolean;
}

export default function AssignmentCards({
  onSelect,
  visible,
}: AssignmentCardsProps) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        >
          <div className="max-w-2xl w-full px-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-1">
                Try Claude Code
              </h2>
              <p className="text-sm text-gray-400">
                Pick a task or type your own question in the terminal below
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {assignments.map((assignment, i) => {
                const Icon = iconMap[assignment.icon] || FileText;
                return (
                  <motion.button
                    key={assignment.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    onClick={() => onSelect(assignment)}
                    className={`text-left p-4 rounded-xl border bg-surface-DEFAULT/80 backdrop-blur transition-all duration-200 hover:bg-surface-100/50 ${categoryColors[assignment.category]}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-4 w-4 text-gray-400" />
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${categoryBadgeColors[assignment.category]}`}
                      >
                        {assignment.category}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-white mb-1">
                      {assignment.title}
                    </div>
                    <div className="text-xs text-gray-500">
                      {assignment.description}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
